import { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import NeilMethodSection from "@/components/NeilMethodSection";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, Leaf, Ruler, Trophy, Home as HomeIcon } from "lucide-react";
import {
  landscapeProducts,
  landscapeImages,
  landscapeKits,
  type LandscapeCategory,
} from "@/lib/landscapeCatalog";

const CATEGORIES: { key: "all" | LandscapeCategory; label: string; icon: typeof Leaf }[] = [
  { key: "all", label: "All Landscape Robots", icon: Leaf },
  { key: "Professional Mowing", label: "Professional Mowing", icon: Trophy },
  { key: "Line Marking", label: "Line Marking", icon: Ruler },
  { key: "Residential Mowing", label: "Residential & Estate", icon: HomeIcon },
];

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const LandscapeRobotics = () => {
  const [active, setActive] = useState<"all" | LandscapeCategory>("all");
  const products =
    active === "all" ? landscapeProducts : landscapeProducts.filter((p) => p.category === active);

  return (
    <>
      <SEO
        title="Landscape & Turf Robotics | Autonomous Mowing & Line Marking"
        description="Neil AI landscape robotics: autonomous mowers and robotic line markers for golf courses, sports fields, municipalities, and estates. Verified specs, financing, and RaaS options."
        keywords="robotic mower, autonomous line marker, golf course robotics, sports field line marking robot, commercial landscaping robots"
      />
      <Navigation />

      <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Hero */}
          <section className="relative overflow-hidden rounded-3xl border border-[hsl(var(--line))]">
            <img
              src={landscapeImages.hero}
              alt="Autonomous landscape robot maintaining a professional turf field"
              className="absolute inset-0 h-full w-full object-cover opacity-40"
              loading="eager"
            />
            <div className="relative bg-gradient-to-r from-background via-background/85 to-background/40 p-8 md:p-14">
              <Badge className="mb-4 bg-primary/15 text-primary border border-primary/30">
                Landscape & Turf Division
              </Badge>
              <h1 className="font-orbitron text-3xl md:text-5xl font-bold max-w-3xl leading-tight">
                The Autonomous Grounds Workforce
              </h1>
              <p className="mt-5 max-w-2xl text-muted-foreground text-base md:text-lg">
                Extend automation beyond the building with intelligent robotic systems for
                commercial mowing, turf management, grounds maintenance and field marking —
                deployed, supported and managed by Neil AI.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full">
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                    Request a Grounds Automation Assessment <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full">
                  <Link to="/industries/landscaping">Landscaping Industry Solutions</Link>
                </Button>
              </div>
            </div>
          </section>

          {/* Value bar */}
          <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { stat: "Up to 90%", label: "Lower labor cost on repetitive mowing and marking" },
              { stat: "50%+", label: "Reduction in paint usage with robotic line marking" },
              { stat: "24/7", label: "Unattended operation with auto-charging and battery swap" },
              { stat: "Fleet", label: "Multi-zone and multi-field management from one platform" },
            ].map((item) => (
              <Card key={item.label} className="glass-effect border-[hsl(var(--line))] p-5">
                <div className="font-orbitron text-2xl font-bold text-primary">{item.stat}</div>
                <p className="mt-2 text-sm text-muted-foreground">{item.label}</p>
              </Card>
            ))}
          </section>

          {/* Category filter */}
          <section className="mt-14">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActive(key)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
                    active === key
                      ? "border-primary/50 bg-primary/15 text-primary"
                      : "border-[hsl(var(--line))] text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => (
                <Card
                  key={p.slug}
                  className="glass-effect border-[hsl(var(--line))] overflow-hidden flex flex-col"
                >
                  <div className="aspect-[16/9] bg-white/5">
                    <img
                      src={p.image}
                      alt={`${p.name} autonomous landscape robot`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <Badge variant="outline" className="w-fit text-[11px]">
                      {p.category}
                    </Badge>
                    <h2 className="mt-3 font-orbitron text-lg font-bold">{p.name}</h2>
                    <p className="mt-1 text-sm text-primary">{p.tagline}</p>
                    <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{p.summary}</p>
                    <ul className="mt-4 space-y-1.5">
                      {p.highlights.slice(0, 3).map((h) => (
                        <li key={h} className="flex gap-2 text-xs text-muted-foreground">
                          <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 flex gap-2 pt-2 mt-auto">
                      <Button asChild size="sm" className="rounded-full flex-1">
                        <Link to={`/robots/landscape/${p.slug}`}>View Specs</Link>
                      </Button>
                      <Button asChild size="sm" variant="outline" className="rounded-full">
                        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                          Get Quote
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Kits */}
          <section className="mt-16">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Attachment Kits</h2>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Turn one multi-function platform into an entire grounds crew. Kits swap in minutes and
              share the same navigation, scheduling, and fleet management systems.
            </p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {landscapeKits.map((kit) => (
                <Card key={kit.name} className="glass-effect border-[hsl(var(--line))] p-5">
                  <h3 className="font-semibold text-sm">{kit.name}</h3>
                  <ul className="mt-3 space-y-1.5">
                    {kit.specs.map((s) => (
                      <li key={s} className="text-xs text-muted-foreground">
                        {s}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </section>

          {/* Solutions */}
          <section className="mt-16 grid gap-6 lg:grid-cols-2">
            <Card className="glass-effect border-[hsl(var(--line))] overflow-hidden">
              <img
                src={landscapeImages.sportsField}
                alt="Robotic mower maintaining a professional sports field"
                className="h-56 w-full object-cover"
                loading="lazy"
              />
              <div className="p-6">
                <h3 className="font-orbitron text-xl font-bold">Golf Course Solution</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  A complete workflow: centimeter-level positioning, 3D course mapping, autonomous
                  mowing across fairways, roughs, greens surrounds and driving ranges, and a data
                  platform for scheduling, monitoring, and maintenance decisions.
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                  <li>• Fairways: RCM01 cylinder mower, Titan</li>
                  <li>• Roughs: RM21 (with dew brush kit), FRX for 30° slopes</li>
                  <li>• Driving ranges: RM21 + ball picker kit</li>
                </ul>
              </div>
            </Card>
            <Card className="glass-effect border-[hsl(var(--line))] overflow-hidden">
              <img
                src={landscapeImages.velaLifestyle}
                alt="Residential robotic mower working on an estate lawn"
                className="h-56 w-full object-cover"
                loading="lazy"
              />
              <div className="p-6">
                <h3 className="font-orbitron text-xl font-bold">Sports Field & Municipal Solution</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Mow and mark full-size fields with the same fleet. Robotic line markers cut paint
                  usage by more than half and free your crew from repetitive layout work before every
                  match day.
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                  <li>• Marking: PaintMaster Pro, Mini, and Ultra</li>
                  <li>• Mowing: Titan, FRX, RM21 multi-zone management</li>
                  <li>• Estates and green spaces: FR4000, FV2000, FL3000, Vela</li>
                </ul>
              </div>
            </Card>
          </section>

          {/* RaaS */}
          <section className="mt-16 rounded-3xl border border-[#FFD700]/25 bg-[#FFD700]/5 p-8 md:p-12">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">
              Robotics-as-a-Service &amp; Financing
            </h2>
            <p className="mt-3 max-w-3xl text-muted-foreground">
              Deploy without a capital project. Neil AI offers monthly RaaS agreements that bundle
              hardware, deployment, mapping, software, and support — plus equipment financing for
              teams that prefer to own. Every deployment includes site assessment, boundary and route
              mapping, crew training, and ongoing service.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  Request Pricing &amp; RaaS Options
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full">
                <Link to="/roi-calculator">Calculate My ROI</Link>
              </Button>
            </div>
          </section>

          <p className="mt-10 text-xs text-muted-foreground">
            Specifications listed are manufacturer-published figures for the corresponding models and
            may vary by configuration, turf conditions, terrain, and regional availability. Neil AI
            Global Solutions provides sales, deployment, integration, training, and support.
          </p>
        </div>
      </main>

      <div className="px-4"><NeilMethodSection compact contextLabel="N.E.I.L.™ GROUNDS ASSESSMENT" contextCopy="Outdoor automation begins with a grounds assessment — acreage, terrain, slopes, boundaries, obstacles, turf requirements, charging locations, and operating windows — before any machine is specified." /></div>
        <Footer />
    </>
  );
};

export default LandscapeRobotics;
